import { useState, useRef, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useForm, FormProvider } from "react-hook-form";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  GALLERY_SOURCE_AD_ACCOUNT,
  GALLERY_SOURCE_AD_ACCOUNT_EMPTY,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
  PLATFORM_PLACEHOLDER,
  PROCESSING_MEDIA_TO_USE,
  SAVING_MEDIA,
} from "../../i18n/keysTranslations";
import { BUTTON_SHADOW } from "../../utils/colors";
import { useTranslationApp } from "../../lib/i18next";
import useNearScreen from "../../hooks/useNearScreen";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useDebounced } from "../../hooks/useDebounce";
import ModalBasicLayout from "./ModalBasicLayout";
import Loader from "../Loaders/Loader";
import Button from "../Buttons/Button";
import Gallery from "../Gallery/Gallery";
import { usePagination } from "../../hooks/usePagination";
import {
  ADS_CAMPAIGN_MEDIA_FROM_AD_ACCOUNT,
  AD_ACCOUNT_PLATFORMS_LABELS,
  GALLERY_AD_ACCOUNT_PLATFORMS,
  GOOGLE,
  META,
} from "../../utils/constants";
import { startGetGalleryAdsPlatforms } from "../../actions/adsCampaigns";
import {
  startAddContentsToList,
  startSaveMediaInStorage,
} from "../../actions/user";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import SelectBusinessAccount from "../Meta/SelectBusinessAccount";
import Select from "../Form/Select";
import { getIntegrationKey } from "../../actions/getters";

const PAGE_SIZE = 10;

const getMediaFromForPlatform = (platform) => {
  if (platform === META) {
    return ADS_CAMPAIGN_MEDIA_FROM_AD_ACCOUNT;
  }
  return GOOGLE;
};

const hasPlatformIntegration = (platform, integrations) => {
  const integrationKey = getIntegrationKey(platform);
  return Object.keys(integrations?.[integrationKey] || {}).length > 0;
};

const ModalGalleryAdAccount = ({
  modalOpen,
  multiple = false,
  onCloseModal,
  onCallbackSelectedItems,
  pathStorage,
  contentFolderID = null,
  compact = false,
}) => {
  const [data, setData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  const { t } = useTranslationApp();
  const { integrations = {} } = useSelectorApp((state) => state.user);

  const galleryRef = useRef(null);
  const abortControllerRef = useRef(null);

  const methods = useForm({
    defaultValues: {
      platform: "",
      integrationID: "",
      adAccountID: "",
    },
  });
  const { control, watch, setValue } = methods;
  const platform = watch("platform");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");

  const availablePlatforms = useMemo(
    () =>
      GALLERY_AD_ACCOUNT_PLATFORMS.filter((item) =>
        hasPlatformIntegration(item, integrations)
      ),
    [integrations]
  );

  const { isNearScreen, fromRef } = useNearScreen({
    distance: "100px",
    once: false,
    disabledTimeout: true,
  });

  const dispatch = useDispatchApp();

  const getNextMedia = useDebounced(async () => {
    if (
      !platform ||
      !integrationID ||
      !adAccountID ||
      loadLastItem ||
      isCalling
    ) {
      return;
    }

    const signal = abortControllerRef.current?.signal;

    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform,
        integrationID,
        adAccountID,
        pageID: undefined,
        lastKey,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: getMediaFromForPlatform(platform),
        signal,
      })
    );
    if (response?.aborted) return;
    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData((prevState) => [...prevState, ...dataFormatted]);
    }
  }, 1000);

  const { lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: getNextMedia,
  });

  const resetGalleryState = () => {
    setData([]);
    setSelectedItems([]);
    setLoadLastItem(false);
    onChangeLastKey(null);
  };

  const getInitialData = async (signal) => {
    if (!platform || !integrationID || !adAccountID) return;

    setLoading(true);
    resetGalleryState();

    const response = await dispatch(
      startGetGalleryAdsPlatforms({
        platform,
        integrationID,
        adAccountID,
        pageID: undefined,
        lastKey: undefined,
        onChangeLastKey,
        onChangeLoadLastItem: setLoadLastItem,
        onChangeIsCalling: setIsCalling,
        limit: PAGE_SIZE,
        from: getMediaFromForPlatform(platform),
        signal,
      })
    );

    if (response?.aborted) return;

    if (response.ok) {
      const dataFormatted = response?.data?.map((media) => ({
        ...media,
        id: media.id,
      }));
      setData(dataFormatted);
    }

    setLoading(false);
  };

  const onChangePlatform = () => {
    setValue("adAccountID", "");
    setValue("integrationID", "");
    resetGalleryState();
  };

  const onChangeAdAccount = (option) => {
    setValue("integrationID", option?.integrationID || "");
    resetGalleryState();
  };

  useEffect(() => {
    abortControllerRef.current?.abort();
    if (!platform || !integrationID || !adAccountID) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    getInitialData(controller.signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [platform, integrationID, adAccountID]);

  useEffect(() => {
    if (isNearScreen && !loadLastItem) getNextMedia();
    // eslint-disable-next-line
  }, [isNearScreen]);

  const onFailedLoadUrl = (index) => {
    setData((prevState) => {
      const newData = [...prevState];
      const itemToRemove = newData[index];
      if (itemToRemove?.id) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter((id) => id !== itemToRemove.id)
        );
      }
      return newData.filter((item, i) => i !== index);
    });
  };

  const onSelectItem = (index) => {
    const item = data[index];
    if (!item || !item.id) return;

    const itemId = item.id;

    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      }
      if (multiple) {
        return [...prevSelected, itemId];
      }
      return [itemId];
    });
  };

  const onSendSelectedItems = async (indexes) => {
    const dataSelectedItems = [];
    const itemsToProcess = indexes
      ? indexes.map((index) => data[index]).filter(Boolean)
      : selectedItems
          .map((id) => data.find((item) => item.id === id))
          .filter(Boolean);

    itemsToProcess.forEach((item) => {
      if (item) {
        dataSelectedItems.push({
          url: item.url,
          from: item.from || "gallery",
          id: item.id,
          userID: item.userID || "",
        });
      }
    });

    const urls = dataSelectedItems.map((item) => item.url);

    setIsUploading(true);
    const response = await dispatch(
      startSaveMediaInStorage({
        urls,
        path: pathStorage,
      })
    );
    if (!response.ok) {
      setIsUploading(false);
      return false;
    }

    const urlsStored = response.data.urls;

    urlsStored.forEach((urlStored, index) => {
      dataSelectedItems[index].url = urlStored;
    });
    await dispatch(
      startAddContentsToList(
        urlsStored.map((url) => ({
          url,
          type: "upload",
          ...(contentFolderID && { folderID: contentFolderID }),
        }))
      )
    );

    setIsUploading(false);

    onCallbackSelectedItems(dataSelectedItems);
    onCloseModal();
  };

  const dataFiltered = data;
  const hasAccountSelected = Boolean(platform && integrationID && adAccountID);

  const selectedItemsAsIndexes = useMemo(() => {
    return dataFiltered
      .map((item, index) =>
        item?.id && selectedItems.includes(item.id) ? index : null
      )
      .filter((index) => index !== null);
  }, [dataFiltered, selectedItems]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={compact ? true : !compact}
      fullScreen={compact ? false : !compact}
      maxWidth={compact ? "lg" : "xl"}
      scroll="paper"
      aria-label={t(MODAL_GALLERY_ARIA_LABEL)}
      sxContent={{
        p: 0,
        pl: 3,
        pb: 2,
        mt: 1,
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          width: 0,
          height: 0,
          background: "transparent",
        },
      }}
      refContent={galleryRef}
      title={t(GALLERY_SOURCE_AD_ACCOUNT)}
    >
      <FormProvider {...methods}>
        <Box
          sx={{
            mb: 4,
            px: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          <Select
            name="platform"
            label={t(PLATFORM_PLACEHOLDER)}
            variant="filled"
            fullWidth={true}
            showEmptyOption={true}
            options={availablePlatforms.map((item) => ({
              value: item,
              label: item,
            }))}
            doTranslate={true}
            actionsChange={onChangePlatform}
            control={control}
            sx={{ width: { xs: "100%", md: "50%" } }}
          />
          {platform && (
            <SelectBusinessAccount
              control={control}
              name="adAccountID"
              platform={platform}
              label={t(AD_ACCOUNT_PLATFORMS_LABELS[platform])}
              fullWidth
              rules={{ required: false }}
              onChangeAction={onChangeAdAccount}
              errors={{}}
              disableSyncEntities={true}
              disableAutoSelect={true}
              sxTextField={{
                width: { xs: "100%", md: "50%" },
              }}
            />
          )}
        </Box>

        {!hasAccountSelected && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              px: 2,
              pb: 6,
              color: "text.secondary",
            }}
          >
            <Typography variant="body1" align="center">
              {t(GALLERY_SOURCE_AD_ACCOUNT_EMPTY)}
            </Typography>
          </Box>
        )}

        {hasAccountSelected && loading && (
          <Loader size={70} hasMessage={true} message={t(LOADING_CONTENT)} />
        )}
        {hasAccountSelected && !loading && (
          <Gallery
            data={dataFiltered}
            onCloseModal={onCloseModal}
            lastItemRef={fromRef}
            loadLastItem={loadLastItem}
            onFailedLoadUrl={onFailedLoadUrl}
            onSelectItem={onSelectItem}
            selectedItems={selectedItemsAsIndexes}
            onSelectPreview={() => {}}
            multiple={multiple}
            showDimensionsOverlay={true}
            showFiltersSelector={false}
            showActionButtons={false}
            sxGallery={{
              px: 0,
            }}
          />
        )}
        {hasAccountSelected &&
          !loading &&
          dataFiltered?.length === 0 &&
          loadLastItem && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: 240,
                px: 3,
                pt: 4,
                pb: 8,
              }}
            >
              <Typography variant="body1" align="center" color="text.secondary">
                {t(MODAL_GALLERY_EMPTY_CONTENT)}
              </Typography>
            </Box>
          )}

        {selectedItems.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: isUploading ? "#FFF" : "transparent",
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              py: 1,
              mt: 2,
              pb: 2,
            }}
          >
            {isUploading && (
              <LinearLoaderWithTime
                labels={[t(PROCESSING_MEDIA_TO_USE), t(SAVING_MEDIA)]}
                time={30 * 1000}
              />
            )}

            {!isUploading && (
              <Button
                color="primary"
                sx={{
                  boxShadow: BUTTON_SHADOW,
                  "&:hover": {
                    boxShadow: BUTTON_SHADOW,
                  },
                }}
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  onSendSelectedItems();
                }}
              >
                {t(BUTTON_ADD_SELECTED_CONTENT)}
              </Button>
            )}
          </Box>
        )}
      </FormProvider>
    </ModalBasicLayout>
  );
};

export default ModalGalleryAdAccount;
